import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { useStore, store } from '../state/patientProductionStore';
import type { Appointment } from '../state/patientProductionStore';
import { patientApi } from '../api';
import RescheduleAppointment from '../components/RescheduleAppointment';
import { useToast } from '../../../components/ui/Toast';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';

type Filter = 'All' | 'Upcoming' | 'Completed' | 'Cancelled';

const statusColor: Record<string, string> = {
  BOOKED: T.teal,
  CONFIRMED: T.success,
  COMPLETED: T.info,
  CANCELLED: T.danger,
  NO_SHOW: T.warning,
};

const typeColor: Record<string, { bg: string; text: string }> = {
  'In-Person': { bg: `${T.navy}14`, text: T.navy },
  'Telemedicine': { bg: `${T.teal}14`, text: T.teal },
};

// Backend status SCHEDULED maps to the local store's BOOKED — everything
// else already lines up.
function toStoreAppointment(d: any): Appointment {
  const appointedAt = new Date(d.appointedAt);
  return {
    id: d.id,
    doctorName: d.providerName || 'Doctor',
    specialty: d.specialty || '',
    hospital: '',
    date: Number.isNaN(appointedAt.getTime()) ? '' : appointedAt.toISOString().split('T')[0],
    time: Number.isNaN(appointedAt.getTime()) ? '' : appointedAt.toISOString().split('T')[1].slice(0, 5),
    type: d.consultationType === 'VIDEO' ? 'Telemedicine' : 'In-Person',
    status: d.status === 'SCHEDULED' ? 'BOOKED' : d.status,
    notes: d.reasonForVisit || undefined,
    createdAt: d.createdAt,
    // Carried through so a reschedule can offer this provider's real slots
    // and show the time being moved.
    providerId: d.providerId ?? null,
    appointedAt: d.appointedAt,
  } as Appointment & { providerId: string | null; appointedAt: string };
}

function filterAppointments(appointments: Appointment[], filter: Filter): Appointment[] {
  switch (filter) {
    case 'Upcoming':
      return appointments.filter(a => a.status === 'BOOKED' || a.status === 'CONFIRMED');
    case 'Completed':
      return appointments.filter(a => a.status === 'COMPLETED');
    case 'Cancelled':
      return appointments.filter(a => a.status === 'CANCELLED');
    default:
      return appointments;
  }
}

export default function PatientAppointments() {
  const navigate = useNavigate();
  const toast = useToast();
  const storeAppointments = useStore(s => s.appointments);
  const [apiAppointments, setApiAppointments] = useState<Appointment[]>([]);
  const [activeFilter, setActiveFilter] = useState<Filter>('All');
  // Which appointment is being moved; only one at a time.
  const [movingId, setMovingId] = useState<string | null>(null);

  const loadApi = useCallback(() => {
    patientApi.getAppointments()
      .then(data => setApiAppointments((data ?? []).map(toStoreAppointment)))
      .catch(() => {});
  }, []);

  useEffect(() => { loadApi(); }, [loadApi]);

  const apiIds = new Set(apiAppointments.map(a => a.id));
  const localById = new Map(storeAppointments.map(a => [a.id, a]));
  // Server-backed bookings exist in both sources under the same id: keep the
  // richer local copy (hospital, picked slot) but trust the server's status.
  const appointments = [
    ...apiAppointments.map(a => {
      const local = localById.get(a.id);
      // The local mirror is richer (hospital, picked slot) but has no
      // providerId — keep both so Reschedule can read it.
      return local
        ? { ...local, status: a.status, providerId: (a as { providerId?: string | null }).providerId ?? null, appointedAt: (a as { appointedAt?: string }).appointedAt }
        : a;
    }),
    ...storeAppointments.filter(a => !apiIds.has(a.id)),
  ];

  const handleCancel = async (id: string) => {
    if (apiIds.has(id)) {
      try {
        await patientApi.cancelAppointment(id);
        toast.success('Appointment cancelled');
        loadApi();
      } catch (e: any) {
        toast.error(e.message || 'Could not cancel appointment.');
      }
    } else {
      store.updateAppointmentStatus(id, 'CANCELLED');
    }
  };

  const filters: Filter[] = ['All', 'Upcoming', 'Completed', 'Cancelled'];
  const filtered = filterAppointments(appointments, activeFilter);

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{ ...GOV_TYPE.pageTitle }}>My Appointments</div>

      {/* Book New Appointment Button */}
      <button
        onClick={() => navigate('/patient/book-doctor')}
        style={{
          width: '100%',
          padding: '12px 0',
          background: T.teal,
          color: T.white,
          fontSize: 13,
          fontWeight: 700,
          fontFamily: T.font,
          border: 'none',
          borderRadius: 12,
          cursor: 'pointer',
        }}
      >
        Book New Appointment
      </button>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 6 }}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            style={{
              flex: 1,
              padding: '6px 0',
              fontSize: 11,
              fontWeight: activeFilter === f ? 700 : 500,
              fontFamily: T.font,
              color: activeFilter === f ? T.white : T.textSec,
              background: activeFilter === f ? T.teal : `${T.ice}`,
              border: activeFilter === f ? 'none' : `1px solid ${T.border}`,
              borderRadius: 20,
              cursor: 'pointer',
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Appointments List or Empty State */}
      {filtered.length === 0 ? (
        <div
          style={{
            background: `${T.teal}08`,
            border: `1px solid ${T.border}`,
            borderRadius: 16,
            padding: 28,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>📅</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 4 }}>
            No appointments yet
          </div>
          <div style={{ fontSize: 10, color: T.textTer, marginBottom: 14 }}>
            Book a doctor visit to see your appointments here.
          </div>
          <button
            onClick={() => navigate('/patient/book-doctor')}
            style={{
              padding: '8px 20px',
              background: T.teal,
              color: T.white,
              fontSize: 11,
              fontWeight: 600,
              fontFamily: T.font,
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
            }}
          >
            Book Your First Appointment
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((appt) => (
            <div
              key={appt.id}
              style={{
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                padding: 14,
                boxShadow: T.shadow,
              }}
            >
              {/* Doctor Info Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{appt.doctorName}</div>
                  <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>{appt.specialty}</div>
                  <div style={{ fontSize: 11, color: T.textTer, marginTop: 1 }}>{appt.hospital}</div>
                </div>
                {/* Status Badge */}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.white,
                    background: statusColor[appt.status] || T.textSec,
                    padding: '3px 10px',
                    borderRadius: 10,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {appt.status.replace('_', ' ')}
                </span>
              </div>

              {/* Date / Time + Type Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: T.textSec }}>
                  {appt.date} &middot; {appt.time}
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: typeColor[appt.type]?.text || T.textSec,
                    background: typeColor[appt.type]?.bg || `${T.bg}`,
                    padding: '2px 8px',
                    borderRadius: 8,
                  }}
                >
                  {appt.type}
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                {appt.type === 'Telemedicine' && (appt.status === 'BOOKED' || appt.status === 'CONFIRMED') && (
                  <button
                    onClick={() => navigate('/patient/my-chat')}
                    style={{
                      flex: 1,
                      padding: '7px 0',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: T.font,
                      color: T.white,
                      background: T.teal,
                      border: 'none',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  >
                    Start Chat
                  </button>
                )}
                {appt.status === 'BOOKED' && (
                  <button
                    onClick={() => handleCancel(appt.id)}
                    style={{
                      flex: 1,
                      padding: '7px 0',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: T.font,
                      color: T.danger,
                      background: `${T.danger}10`,
                      border: `1px solid ${T.danger}30`,
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                )}
                {appt.status === 'BOOKED' && apiIds.has(appt.id) && (
                  <button
                    onClick={() => setMovingId(movingId === appt.id ? null : appt.id)}
                    style={{
                      flex: 1,
                      padding: '7px 0',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: T.font,
                      color: T.teal,
                      background: `${T.teal}10`,
                      border: `1px solid ${T.teal}30`,
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  >
                    {movingId === appt.id ? 'Close' : 'Reschedule'}
                  </button>
                )}
              </div>

              {movingId === appt.id && (
                <RescheduleAppointment
                  appointmentId={appt.id}
                  providerId={(appt as { providerId?: string | null }).providerId ?? null}
                  currentAt={(appt as { appointedAt?: string }).appointedAt ?? appt.date}
                  onDone={() => { setMovingId(null); toast.success('Appointment moved'); loadApi(); }}
                  onCancel={() => setMovingId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
